import { CurrencyPipe, DatePipe, Location } from '@angular/common';
import {
  Directive,
  HostListener,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseDto } from '@authority-labs/api-interfaces';
import { QueryJoin, RequestQueryBuilder } from '@nestjsx/crud-request';
import { cloneDeep } from 'lodash';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Observable, Subject } from 'rxjs';
import { AuthenticationService } from '../auth/authentication.service';
import { AlertDialogService } from '../shared/dialogs/alert-dialog/alert-dialog.service';
import { FieldTypePipe } from '../shared/pipes/field-type/field-type.pipe';
import { AppPermissionsService } from '../shared/services/permissions/permissions.service';
import { OrderStatusPipe } from '../shared/pipes/order-status/order-status.pipe';
import { UsernamePipe } from '../shared/pipes/username/username.pipe';
import { LayoutService } from '../shared/services';
import { ConfigurationService } from '../shared/services/configuration/configuration.service';
import { CrudService } from '../shared/services/crud/crud.service';
import { ComponentCanDeactivate } from './component-can-deactivate';

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class CrudDetailsComponent<T extends BaseDto>
  extends ComponentCanDeactivate
  implements OnInit, OnDestroy
{
  @ViewChild('form') form: NgForm;

  extraParams: QueryJoin[] = [];
  entity: T;
  origEntity: T;
  submitting = false;
  isNew: boolean;
  isDialog: boolean;
  isMobile: boolean;
  tabIndex = 0;
  readonly = true;

  route: ActivatedRoute;
  message: NzMessageService;
  configurationService: ConfigurationService;
  usernamePipe: UsernamePipe;
  datePipe: DatePipe;
  currencyPipe: CurrencyPipe;
  orderStatusPipe: OrderStatusPipe;
  fieldTypePipe: FieldTypePipe;
  layoutService: LayoutService;
  router: Router;
  modal: NzModalService;
  location: Location;
  alertDialogService: AlertDialogService;
  authService: AuthenticationService;
  appPermissionsService: AppPermissionsService;

  componentDestroyed$: Subject<boolean> = new Subject();

  /** Permission key(s) required to edit this entity. Subclasses can override. */
  editPermissionKeys: string[] = [];

  queryParams: Record<string, string> = {};

  get title(): string {
    return '';
  }

  get statsStyle() {
    return this.isMobile ? { fontSize: '14px' } : {};
  }

  constructor(injector: Injector, private crudService: CrudService<T>) {
    super();
    this.route = injector.get(ActivatedRoute);
    this.message = injector.get(NzMessageService);
    this.layoutService = injector.get(LayoutService);
    this.router = injector.get(Router);
    this.modal = injector.get(NzModalService);
    this.location = injector.get(Location);

    this.configurationService = injector.get(ConfigurationService);
    this.usernamePipe = injector.get(UsernamePipe);
    this.datePipe = injector.get(DatePipe);
    this.currencyPipe = injector.get(CurrencyPipe);
    this.orderStatusPipe = injector.get(OrderStatusPipe);
    this.fieldTypePipe = injector.get(FieldTypePipe);
    this.alertDialogService = injector.get(AlertDialogService);
    this.authService = injector.get(AuthenticationService);
    this.appPermissionsService = injector.get(AppPermissionsService);

    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  ngOnInit() {
    this.readonly =
      this.editPermissionKeys.length > 0
        ? !this.appPermissionsService.hasAny(...this.editPermissionKeys)
        : false;

    this.layoutService.isMobile.subscribe(
      (isMobile) => (this.isMobile = isMobile)
    );

    this.route.params.subscribe((params) => {
      const id = params['id'];
      this.isNew = id === 'new';
      this.isDialog = id === undefined;

      if (this.isDialog) {
        this.isNew = !this.entity?.id;
        if (this.isNew) {
          this.createNewEntity();
        } else {
          this.init(this.entity);
          this.afterInit(this.entity);
        }
      } else if (this.isNew) {
        this.route.queryParams.subscribe((queryParams) => {
          this.queryParams = queryParams;
          this.createNewEntity(queryParams);
        });
      } else {
        const query = RequestQueryBuilder.create({
          join: this.extraParams
        }).query();

        this.crudService.getOne(id, query).subscribe(
          (_entity) => {
            this.init(_entity);
            this.saveCurrentState();
            this.afterInit(_entity);
          },
          (error) => {
            console.log(error);
            let content: string;
            this.modal.warning({
              nzTitle: 'תקלה',
              nzContent: content
            });
          }
        );
      }
    });

    this.route.queryParams.subscribe((params) => {
      this.queryParams = params;
    });
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  @HostListener('window:beforeunload', ['$event'])
  public unloadNotification($event: { returnValue: boolean }) {
    if (!this.canDeactivate()) {
      $event.returnValue = true;
    }
  }

  canDeactivate(): Observable<boolean> | boolean {
    return !this.form?.dirty;
  }

  init(_entity: T): void {
    this.entity = this.toClass({ ...this.entity, ..._entity });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  afterInit(_entity?: T): void {}

  save(): void {
    this.submitting = true;
    const entity = this.prepareBeforeSave(this.entity);

    // const obj = this.getModifiedFields(this.origEntity, entity);

    // const obj2 = this.clearEmpties({ ...obj });
    // console.log(obj2);

    const query = RequestQueryBuilder.create({
      join: this.extraParams
    }).query();

    if (this.isNew) {
      this.crudService
        .createOne(entity, query)
        .subscribe(this.onSuccess, this.onError);
    } else {
      this.crudService
        .updateOne(this.entity.id, entity, query)
        .subscribe(this.onSuccess, this.onError);
    }
  }

  prepareBeforeSave(entity: T): T {
    return this.toClass({ ...entity });
  }

  toClass(entity: T): T {
    return this.crudService.plainToClass({ ...entity });
  }

  cancel(): void {
    this.submitting = false;
    if (this.isNew) {
      this.location.back();
    } else {
      this.entity = this.toClass(cloneDeep(this.origEntity));
      this.init(this.entity);

      this.markAsPristine();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  createNewEntity(queryParams?: Record<string, string>): void {
    this.entity = new BaseDto() as T;
  }

  markFormAsDirty(): void {
    if (this.form && this.form.form) {
      this.form.form.markAsDirty();
    }
  }

  saveCurrentState(): void {
    this.origEntity = cloneDeep(this.entity);
  }

  private clearEmpties(obj: BaseDto): BaseDto {
    for (const key in obj) {
      if (
        !obj[key] ||
        typeof obj[key] !== 'object' ||
        Array.isArray(obj[key]) ||
        obj[key] instanceof Date
      ) {
        continue;
      }

      if (Object.keys(obj[key]).length === 0) {
        delete obj[key];
      }
    }

    return obj;
  }

  protected onSuccess = (resp: T) => {
    if (this.isDialog) {
      this.onDialogSaveSuccess(resp);
    } else {
      this.submitting = false;
      this.init(resp);
      this.saveCurrentState();
      this.markAsPristine();
      this.message.create('success', 'הפעולה נשמרה בהצלחה');
      if (this.isNew) {
        this.router.navigate(['../', resp.id], { relativeTo: this.route });
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onDialogSaveSuccess(resp: T) {
    this.submitting = false;
    this.message.create('success', 'הפעולה נשמרה בהצלחה');
  }

  protected onError = (error: unknown) => {
    console.error(error);
    this.submitting = false;
    this.message.create('error', 'הפעולה נכשלה', {
      nzDuration: 10000
    });
  };

  protected markAsPristine(): void {
    if (this.form) {
      this.form.form.markAsPristine();
    }
  }

  // private getModifiedFields(a1: any, a2: any): string[] {
  //   function compareObjects(obj1: any, obj2: any, path = '') {
  //     const modifiedFields = [];
  //     for (const key in obj2) {
  //       if (!Object.prototype.hasOwnProperty.call(obj1, key)) {
  //         modifiedFields.push(path + key);
  //       } else if (
  //         typeof obj2[key] === 'object' &&
  //         obj2[key] !== null &&
  //         !Array.isArray(obj2[key])
  //       ) {
  //         const nestedModifiedFields = compareObjects(
  //           obj1[key],
  //           obj2[key],
  //           path + key + '.'
  //         );
  //         modifiedFields.push(...nestedModifiedFields);
  //       } else if (Array.isArray(obj2[key])) {
  //         if (
  //           JSON.stringify(obj1[key].sort()) !==
  //           JSON.stringify(obj2[key].sort())
  //         ) {
  //           modifiedFields.push(path + key);
  //         }
  //       } else if (obj1[key] !== obj2[key]) {
  //         modifiedFields.push(path + key);
  //       }
  //     }
  //     return modifiedFields;
  //   }

  //   return compareObjects(a1, a2);
  // }
}
